/**
 * Message Contract Framework — single source of truth for cross-process message
 * SHAPES, with a tiny dependency-free validator and dev/test-only boundary checks.
 *
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ THIS FILE IS BYTE-IDENTICAL in frontend/core/ and desktop/core/.         │
 * │ The monorepo has no shared package (separate node_modules), so producer  │
 * │ and consumer each keep a copy. `contract-lint` fails the build if the    │
 * │ two copies diverge — edit BOTH, or run the copy step, never just one.    │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * Loads in three environments from ONE source (no `import`/`export` keywords):
 *   - Frontend (Vite/ESM): imported for side-effect; consumed via the thin
 *     `contracts.esm.js` re-export shim, or `globalThis.InquisitorContracts`.
 *   - Desktop renderer (window <script> / VM test loader): `window.InquisitorContracts`.
 *   - Desktop main process (CJS): `require('../core/contracts.js')` → module.exports.
 *
 * What it catches: SHAPE DRIFT (wrong field name, missing required field, wrong
 * type/enum). What it does NOT catch: semantic wrongness (a valid-but-wrong value).
 * See MESSAGE-CONTRACTS.md and the Phase 6 invariant layer (buildUrlBlockPattern +
 * property tests) for the semantic half.
 */
;(function (global, factory) {
  const api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') window.InquisitorContracts = api;
  if (typeof globalThis !== 'undefined') globalThis.InquisitorContracts = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  // ======================================================================
  // Schema builders — small, readable descriptors. A maintainer eyeballs a
  // contract; there is no class hierarchy, just plain objects.
  // ======================================================================

  const S = {
    object: (fields, opts) => Object.assign({ t: 'object', fields: fields || {} }, opts),
    string: (opts) => Object.assign({ t: 'string' }, opts),
    number: (opts) => Object.assign({ t: 'number' }, opts),
    boolean: (opts) => Object.assign({ t: 'boolean' }, opts),
    enum: (values, opts) => Object.assign({ t: 'enum', values: values }, opts),
    array: (items, opts) => Object.assign({ t: 'array', items: items }, opts),
    any: () => ({ t: 'any' }),
  };

  // Field modifiers.
  const optional = (schema) => Object.assign({}, schema, { optional: true });
  const nullable = (schema) => Object.assign({}, schema, { nullable: true });
  // Explicit escape hatch for a genuinely dynamic payload. Visible in review.
  const passthrough = () => ({ t: 'any', __optOut: true });

  // ======================================================================
  // Validator
  // ======================================================================

  function _check(schema, value, path, errors) {
    if (!schema) return;

    if (value === undefined) {
      if (!schema.optional) errors.push({ path: path, msg: 'missing required field (expected ' + schema.t + ')' });
      return;
    }
    // `any` accepts any DEFINED value (including null). Presence is still enforced
    // above, so a required S.any() field must be present but may be any type.
    if (schema.t === 'any') return;
    if (value === null) {
      if (!schema.nullable && !schema.optional) errors.push({ path: path, msg: 'null not allowed (expected ' + schema.t + ')' });
      return;
    }

    switch (schema.t) {
      case 'string':
        if (typeof value !== 'string') errors.push({ path: path, msg: 'expected string, got ' + typeof value });
        return;
      case 'number':
        if (typeof value !== 'number' || Number.isNaN(value)) errors.push({ path: path, msg: 'expected number, got ' + (typeof value === 'number' ? 'NaN' : typeof value) });
        return;
      case 'boolean':
        if (typeof value !== 'boolean') errors.push({ path: path, msg: 'expected boolean, got ' + typeof value });
        return;
      case 'enum':
        if (schema.values.indexOf(value) === -1) errors.push({ path: path, msg: 'expected one of [' + schema.values.join(', ') + '], got ' + JSON.stringify(value) });
        return;
      case 'array':
        if (!Array.isArray(value)) { errors.push({ path: path, msg: 'expected array, got ' + typeof value }); return; }
        for (let i = 0; i < value.length; i++) _check(schema.items, value[i], path + '[' + i + ']', errors);
        return;
      case 'object':
        if (typeof value !== 'object' || Array.isArray(value)) { errors.push({ path: path, msg: 'expected object, got ' + (Array.isArray(value) ? 'array' : typeof value) }); return; }
        for (const key in schema.fields) {
          if (Object.prototype.hasOwnProperty.call(schema.fields, key)) {
            _check(schema.fields[key], value[key], path + '.' + key, errors);
          }
        }
        if (schema.strict) {
          for (const key in value) {
            if (Object.prototype.hasOwnProperty.call(value, key) && !Object.prototype.hasOwnProperty.call(schema.fields, key)) {
              errors.push({ path: path + '.' + key, msg: 'unexpected field (strict mode)' });
            }
          }
        }
        return;
      default:
        return;
    }
  }

  /**
   * Validate a value against a schema.
   * @returns {{ ok: boolean, errors: Array<{path,msg}> }}
   */
  function validate(schema, value) {
    if (schema && schema.__optOut) return { ok: true, errors: [] };
    const errors = [];
    _check(schema, value, '$', errors);
    return { ok: errors.length === 0, errors: errors };
  }

  // ======================================================================
  // Registries — keyed by the wire identifier.
  // ======================================================================

  // Shared url-block request shapes (reused by DataChannel + IPC registries).
  const URL_BLOCK_ADD_REQUEST = S.object({
    type: S.enum(['domain', 'url']),
    target: S.string(),
    pattern: S.string(),
    reason: optional(S.string()),
    source: optional(S.string()),
  });
  const URL_BLOCK_ADD_RESPONSE = S.object({
    success: S.boolean(),
    entry: optional(nullable(S.object({}))),
    duplicate: optional(S.boolean()),
    delivered: optional(S.boolean()),   // Phase 6: reached a live extension now
    queued: optional(S.boolean()),      // Phase 6: persisted, will apply on reconnect
    error: optional(S.string()),
  });
  const URL_BLOCK_REMOVE_REQUEST = S.object({ id: S.string() });
  const URL_BLOCK_REMOVE_RESPONSE = S.object({
    success: S.boolean(),
    delivered: optional(S.boolean()),
    queued: optional(S.boolean()),
    error: optional(S.string()),
  });
  const URL_BLOCK_LIST_REQUEST = S.object({});
  const URL_BLOCK_LIST_RESPONSE = S.object({
    success: S.boolean(),
    entries: optional(S.array(S.object({}))),
    error: optional(S.string()),
  });

  // DataChannel feature messages: feature → cmd → { request, response }.
  const DATACHANNEL = {
    interrogation: {
      get_settings: {
        request: S.object({}),
        response: S.object({
          success: S.boolean(),
          settings: optional(nullable(S.object({}))),
          error: optional(S.string()),
        }),
      },
      save_settings: {
        request: S.object({ settings: S.object({}) }),
        response: S.object({ success: S.boolean(), error: optional(S.string()) }),
      },
    },
    'browser-extension': {
      url_block_add: { request: URL_BLOCK_ADD_REQUEST, response: URL_BLOCK_ADD_RESPONSE },
      url_block_remove: { request: URL_BLOCK_REMOVE_REQUEST, response: URL_BLOCK_REMOVE_RESPONSE },
      url_block_list: { request: URL_BLOCK_LIST_REQUEST, response: URL_BLOCK_LIST_RESPONSE },
    },
    'time-limit': {
      // Every time-limit response carries { success, error? }; some add data.
      get_limits: {
        request: S.object({}),
        response: S.object({ success: S.boolean(), limits: optional(S.array(S.object({}))), error: optional(S.string()) }),
      },
      get_usage: {
        request: S.object({}),
        response: S.object({ success: S.boolean(), usage: optional(S.object({})), error: optional(S.string()) }),
      },
      get_enforcement_state: {
        request: S.object({}),
        response: S.object({ success: S.boolean(), error: optional(S.string()) }),
      },
      // add_limit requires targetName (the desktop rejects it otherwise); the rest
      // of the limit config passes through.
      add_limit: {
        request: S.object({ targetName: S.string() }),
        response: S.object({ success: S.boolean(), error: optional(S.string()) }),
      },
      update_limit: {
        request: S.object({ id: S.number(), config: optional(S.object({})) }),
        response: S.object({ success: S.boolean(), error: optional(S.string()) }),
      },
      remove_limit: {
        request: S.object({ id: S.number() }),
        response: S.object({ success: S.boolean(), error: optional(S.string()) }),
      },
      start: {
        request: S.object({}),
        response: S.object({ success: S.boolean(), error: optional(S.string()) }),
      },
      stop: {
        request: S.object({}),
        response: S.object({ success: S.boolean(), error: optional(S.string()) }),
      },
    },
    'keyword-database': {
      get_all_groups: {
        request: S.object({}),
        response: S.object({ success: S.boolean(), groups: optional(S.array(S.object({}))), error: optional(S.string()) }),
      },
      get_enabled_groups: {
        request: S.object({}),
        response: S.object({ success: S.boolean(), groups: optional(S.array(S.object({}))), error: optional(S.string()) }),
      },
      // The whole payload is the group object (id, name, keywords, enabled,
      // enforceAction, lockMinutes…); shape-validated as an object, fields pass through.
      upsert_group: {
        request: S.object({}),
        response: S.object({ success: S.boolean(), group: optional(S.object({})), error: optional(S.string()) }),
      },
      delete_group: {
        request: S.object({ groupId: S.string() }),   // group ids are string slugs ('adult-content')
        response: S.object({ success: S.boolean(), deleted: optional(S.boolean()), error: optional(S.string()) }),
      },
      check_keywords: {
        request: S.object({ text: S.string() }),
        response: S.object({ success: S.boolean(), matched: optional(S.boolean()), groups: optional(S.array(S.object({}))), error: optional(S.string()) }),
      },
      scan_content: {
        request: S.object({ content: S.string(), title: optional(S.string()), url: optional(S.string()) }),
        response: S.object({ success: S.boolean(), matched: optional(S.boolean()), groups: optional(S.array(S.object({}))), error: optional(S.string()) }),
      },
    },
    // Time-gated monitoring consent. The desktop owns ALL state; the frontend
    // can read status, provision the partner-held revoke code, and nudge the
    // desktop to show its consent prompt. Grant/renew/revoke/self-release have
    // NO DataChannel representation by design — they only exist on the
    // desktop's own overlay windows.
    consent: {
      get_status: {
        request: S.object({}),
        response: S.object({
          success: S.boolean(),
          state: optional(nullable(S.object({}))),
          error: optional(S.string()),
        }),
      },
      set_revoke_code: {
        request: S.object({ code: S.string(), currentCode: optional(S.string()) }),
        response: S.object({ success: S.boolean(), error: optional(S.string()) }),
      },
      prompt_consent: {
        request: S.object({ mode: optional(S.string()) }),
        response: S.object({ success: S.boolean(), error: optional(S.string()) }),
      },
    },
    economy: (() => {
      // Every economy response is { success, error? } (+ optional data on reads).
      const OK = S.object({ success: S.boolean(), error: optional(S.string()) });
      const READ = (field) => S.object({ success: S.boolean(), [field]: optional(S.any()), error: optional(S.string()) });
      // ids (taskId/gemId/listingId/id) are opaque across economy — accept any
      // non-undefined value rather than guess number vs string.
      return {
        get_currencies: { request: S.object({}), response: READ('currencies') },
        add_currency: { request: S.object({ currencyType: S.string(), displayName: optional(S.string()), symbol: optional(S.string()), description: optional(S.string()) }), response: OK },
        get_balances: { request: S.object({ includeHidden: optional(S.boolean()) }), response: READ('balances') },
        set_visibility: { request: S.object({ currencyType: S.string(), visible: S.boolean() }), response: OK },
        get_tasks: { request: S.object({}), response: READ('tasks') },
        get_task: { request: S.object({ taskId: S.any() }), response: READ('task') },
        submit_task: { request: S.object({ taskId: S.any(), proofData: optional(S.any()) }), response: OK },
        approve_task: { request: S.object({ taskId: S.any() }), response: OK },
        reject_task: { request: S.object({ taskId: S.any(), reason: optional(S.string()) }), response: OK },
        delete_task: { request: S.object({ taskId: S.any() }), response: OK },
        get_stats: { request: S.object({}), response: READ('stats') },
        set_socket_count: { request: S.object({ count: S.number() }), response: OK },
        award_gem: { request: S.object({ gem: S.object({}) }), response: OK },
        remove_gem: { request: S.object({ gemId: S.any() }), response: OK },
        get_display_state: { request: S.object({}), response: READ('state') },
        set_desktop_clickthrough: { request: S.object({ enabled: S.boolean() }), response: OK },
        create_purchase: { request: S.object({ listingId: S.any() }), response: OK },
        approve_purchase: { request: S.object({ id: S.any() }), response: OK },
        reject_purchase: { request: S.object({ id: S.any(), reason: optional(S.string()) }), response: OK },
        get_purchases: { request: S.object({}), response: READ('purchases') },
      };
    })(),
  };

  // Electron IPC: 'feature:channel' → { request, response }.
  const IPC = {
    'url-block:add': { request: URL_BLOCK_ADD_REQUEST, response: URL_BLOCK_ADD_RESPONSE },
    'url-block:remove': { request: URL_BLOCK_REMOVE_REQUEST, response: URL_BLOCK_REMOVE_RESPONSE },
    'url-block:list': { request: URL_BLOCK_LIST_REQUEST, response: URL_BLOCK_LIST_RESPONSE },
  };

  // Extension WebSocket: type → Schema (the whole message, `type` included).
  const EXTENSION_WS = {
    set_redirect_rules: S.object({
      type: S.enum(['set_redirect_rules']),
      rules: S.array(S.object({
        id: optional(S.string()),
        pattern: S.string(),
        replacement: S.string(),
      })),
    }),
  };

  function getDataChannelContract(feature, cmd) {
    const entry = DATACHANNEL[feature];
    if (!entry) return null;
    const isResp = /_response$/.test(cmd);
    const base = isResp ? cmd.replace(/_response$/, '') : cmd;
    const c = entry[base];
    if (!c) return null;
    return { base: base, isResponse: isResp, schema: isResp ? c.response : c.request };
  }

  // ======================================================================
  // Runtime boundary validation — dev/test only, zero cost in production.
  // ======================================================================

  /**
   * 'throw'  — tests: a shape mismatch fails the test loudly.
   * 'warn'   — dev: console.error + structured detail, never throws.
   * 'off'    — production: no-op.
   * Override with globalThis.__CONTRACT_VALIDATION__ = 'throw'|'warn'|'off'.
   */
  function boundaryMode() {
    if (typeof globalThis !== 'undefined' && globalThis.__CONTRACT_VALIDATION__) {
      return globalThis.__CONTRACT_VALIDATION__;
    }
    let env;
    // Bare `process.env.NODE_ENV` so Vite statically inlines it in the browser
    // build (→ "production" ⇒ off). try/catch guards a raw browser with no shim.
    try { env = process.env.NODE_ENV; } catch (e) { env = undefined; }
    if (env === 'production') return 'off';
    if (env === 'test') return 'throw';
    return 'warn';
  }

  function _report(mode, feature, cmd, context, errors) {
    const detail = errors.map((e) => e.path + ': ' + e.msg).join('; ');
    const msg = '[contract] ' + (context || '') + ' ' + feature + '/' + cmd + ' payload violates contract → ' + detail;
    if (mode === 'throw') {
      const err = new Error(msg);
      err.contractErrors = errors;
      throw err;
    }
    try { console.error(msg, errors); } catch (e) { /* no console */ }
  }

  /**
   * Validate a DataChannel message payload against its registered contract.
   * Direction is auto-detected from the `_response` suffix, so the SAME check
   * runs whether the frontend sends a request or the desktop receives it — a
   * request schema and its consumer can't drift.
   *
   * Unregistered feature/cmd → no-op (the registry is opt-in; Phase 3's ratchet
   * is what forces NEW cmds to register).
   *
   * @returns {{ ok: boolean, skipped?: string, errors?: Array }}
   */
  function validateBoundary(opts) {
    const feature = opts.feature;
    const cmd = opts.cmd;
    const payload = opts.payload;
    const context = opts.context;
    const mode = boundaryMode();
    if (mode === 'off') return { ok: true, skipped: 'off' };

    const found = getDataChannelContract(feature, cmd);
    if (!found) return { ok: true, skipped: 'unregistered' };
    if (!found.schema || found.schema.__optOut) return { ok: true, skipped: 'no-schema' };

    const res = validate(found.schema, payload);
    if (!res.ok) _report(mode, feature, cmd, context, res.errors);
    return res;
  }

  /** Validate an IPC invoke payload / handler return against the IPC registry. */
  function validateIpc(channel, value, kind, context) {
    const mode = boundaryMode();
    if (mode === 'off') return { ok: true, skipped: 'off' };
    const c = IPC[channel];
    if (!c) return { ok: true, skipped: 'unregistered' };
    const schema = kind === 'response' ? c.response : c.request;
    if (!schema || schema.__optOut) return { ok: true, skipped: 'no-schema' };
    const res = validate(schema, value);
    if (!res.ok) _report(mode, 'ipc', channel + ':' + kind, context, res.errors);
    return res;
  }

  /** Validate an extension WebSocket message against the WS registry (by `type`). */
  function validateExtensionWs(message, context) {
    const mode = boundaryMode();
    if (mode === 'off') return { ok: true, skipped: 'off' };
    const type = message && message.type;
    const schema = EXTENSION_WS[type];
    if (!schema) return { ok: true, skipped: 'unregistered' };
    const res = validate(schema, message);
    if (!res.ok) _report(mode, 'extension-ws', type, context, res.errors);
    return res;
  }

  // ======================================================================
  // Phase 6 — semantic builders (shared value logic that must NOT diverge
  // across producers). Duplicated only via this byte-identical file.
  // ======================================================================

  /** Normalize a domain input: strip protocol, www, and any path. */
  function normalizeBlockDomain(domain) {
    return String(domain).trim().toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/.*$/, '');
  }

  /**
   * Canonical anchored full-URL block pattern for a domain, used by BOTH the
   * flag-review path (frontend BlockActionModal) and the keyword-auto path
   * (desktop control-advisor). Anchored so the extension's redirect replaces the
   * WHOLE url with about:blank (a partial match corrupts it to
   * "https://about:blank/…", which is the bug that left x.com reachable).
   */
  function buildUrlBlockPattern(domain) {
    const d = normalizeBlockDomain(domain);
    const esc = d.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return '^https?://([^/]*\\.)?' + esc + '(/.*)?$';
  }

  return {
    S: S,
    optional: optional,
    nullable: nullable,
    passthrough: passthrough,
    validate: validate,
    DATACHANNEL: DATACHANNEL,
    IPC: IPC,
    EXTENSION_WS: EXTENSION_WS,
    getDataChannelContract: getDataChannelContract,
    boundaryMode: boundaryMode,
    validateBoundary: validateBoundary,
    validateIpc: validateIpc,
    validateExtensionWs: validateExtensionWs,
    normalizeBlockDomain: normalizeBlockDomain,
    buildUrlBlockPattern: buildUrlBlockPattern,
  };
});
